import { Table as ConnectTable, TextField } from '@lego/connect-components-react';
import type React from 'react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TableSkeleton } from '@/components/table-skeleton';

export type TableColumn<T = Record<string, unknown>> = {
  key: string;
  label: string;
  sortable?: boolean;
  render?: (value: React.ReactNode, item: T) => React.ReactNode;
  accessor?: (item: T) => unknown;
};

type TableProps<T = Record<string, unknown>> = {
  data: T[];
  columns: TableColumn<T>[];
  onRowClick?: (item: T) => void;
  loading?: boolean;
  emptyMessage?: string;
  className?: string;
  searchable?: boolean;
  searchPlaceholder?: string;
  totalCount?: number;
  ariaLabel?: string;
};

type SortDirection = 'asc' | 'desc' | null;

const INTERACTIVE_SELECTOR = 'button, a, input, select, textarea, [role="button"]';

const isNestedInteractive = (target: EventTarget | null, current: EventTarget | null) => {
  const element =
    target instanceof HTMLElement ? target : target instanceof Node ? target.parentElement : null;
  if (!element) return false;
  const interactive = element.closest(INTERACTIVE_SELECTOR);
  return interactive !== null && interactive !== current;
};

const compareColumnValues = (aValue: unknown, bValue: unknown, direction: 'asc' | 'desc') => {
  if (aValue === bValue) return 0;

  const comparison =
    typeof aValue === 'number' && typeof bValue === 'number'
      ? aValue - bValue
      : String(aValue).localeCompare(String(bValue), undefined, {
          numeric: true,
          sensitivity: 'base',
        });

  return direction === 'asc' ? comparison : -comparison;
};

export const Table = <T extends Record<string, unknown>>({
  data,
  columns,
  onRowClick,
  loading = false,
  emptyMessage,
  className = '',
  searchable = true,
  searchPlaceholder,
  totalCount,
  ariaLabel,
}: TableProps<T>) => {
  const [sortColumn, setSortColumn] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<SortDirection>(null);
  const [globalSearch, setGlobalSearch] = useState('');
  const { t } = useTranslation();
  const resolvedEmptyMessage = emptyMessage ?? t('table.display.noData');
  const resolvedSearchPlaceholder = searchPlaceholder ?? t('table.display.search');
  const tableAriaLabel = ariaLabel?.trim() || t('table.display.ariaLabel');

  const filterData = useMemo(() => {
    if (!globalSearch.trim()) return data;

    return data.filter((item) =>
      columns.some((column) => {
        const value = column.accessor ? column.accessor(item) : item[column.key];
        return String(value).toLowerCase().includes(globalSearch.toLowerCase());
      })
    );
  }, [data, columns, globalSearch]);

  const sortData = useMemo(() => {
    if (!sortColumn || !sortDirection) return filterData;

    return [...filterData].sort((a, b) => {
      const column = columns.find((col) => col.key === sortColumn);
      const aValue = column?.accessor ? column.accessor(a) : a[sortColumn];
      const bValue = column?.accessor ? column.accessor(b) : b[sortColumn];
      return compareColumnValues(aValue, bValue, sortDirection);
    });
  }, [filterData, columns, sortColumn, sortDirection]);

  const filteredAndSortedData = sortData;

  const handleSort = (columnKey: string) => {
    const column = columns.find((col) => col.key === columnKey);
    if (!column?.sortable) return;

    if (sortColumn === columnKey) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : sortDirection === 'desc' ? null : 'asc');
      if (sortDirection === 'desc') {
        setSortColumn(null);
      }
    } else {
      setSortColumn(columnKey);
      setSortDirection('asc');
    }
  };

  if (loading) {
    return <TableSkeleton columns={columns.length} />;
  }

  return (
    <div className={`w-full ${className}`}>
      {searchable && (
        <div className="mb-300">
          <div className="w-full max-w-md">
            <TextField
              value={globalSearch}
              onChange={(_e, value) => setGlobalSearch(value)}
              placeholder={resolvedSearchPlaceholder}
              aria-label={resolvedSearchPlaceholder}
              size="medium"
            />
          </div>
        </div>
      )}

      <ConnectTable.Container scrollable>
        <ConnectTable striped aria-label={tableAriaLabel}>
          <ConnectTable.Header>
            <ConnectTable.Row>
              {columns.map((column) => (
                <ConnectTable.HeaderCell
                  key={column.key}
                  sortDirection={sortColumn === column.key && sortDirection ? sortDirection : false}
                  onClick={column.sortable ? () => handleSort(column.key) : undefined}
                >
                  {column.sortable ? (
                    <button
                      type="button"
                      className="min-w-0 cursor-pointer border-0 bg-transparent p-0 text-left whitespace-normal text-inherit"
                      onClick={(event) => {
                        event.stopPropagation();
                        handleSort(column.key);
                      }}
                    >
                      {column.label}
                    </button>
                  ) : (
                    column.label
                  )}
                </ConnectTable.HeaderCell>
              ))}
            </ConnectTable.Row>
          </ConnectTable.Header>
          <ConnectTable.Body>
            {filteredAndSortedData.length === 0 ? (
              <ConnectTable.Row className="connect-table-row--empty-state">
                <ConnectTable.Cell colSpan={columns.length}>
                  <ConnectTable.EmptyState title={resolvedEmptyMessage} />
                </ConnectTable.Cell>
              </ConnectTable.Row>
            ) : (
              filteredAndSortedData.map((item, index) => {
                const itemKey =
                  item && typeof item === 'object' && 'id' in item
                    ? `item-${String(item.id)}`
                    : `row-${index}`;

                return (
                  <ConnectTable.Row
                    key={itemKey}
                    clickable={Boolean(onRowClick)}
                    tabIndex={onRowClick ? 0 : undefined}
                    onClick={(event) => {
                      if (!onRowClick || isNestedInteractive(event.target, event.currentTarget)) {
                        return;
                      }
                      onRowClick(item);
                    }}
                    onKeyDown={(event) => {
                      if (!onRowClick) return;
                      if (event.key !== 'Enter' && event.key !== ' ') return;
                      if (isNestedInteractive(event.target, event.currentTarget)) return;
                      event.preventDefault();
                      onRowClick(item);
                    }}
                  >
                    {columns.map((column) => {
                      const value = column.accessor ? column.accessor(item) : item[column.key];
                      const displayValue = column.render
                        ? column.render(value as React.ReactNode, item)
                        : String(value);

                      return (
                        <ConnectTable.Cell key={`cell-${column.key}`} className="whitespace-normal">
                          {displayValue}
                        </ConnectTable.Cell>
                      );
                    })}
                  </ConnectTable.Row>
                );
              })
            )}
          </ConnectTable.Body>
        </ConnectTable>
      </ConnectTable.Container>

      <div className="mt-200 text-body-xs text-core-slate-700">
        {t('table.display.showingItems', {
          count: filteredAndSortedData.length,
          total: totalCount ?? data.length,
        })}
      </div>
    </div>
  );
};
